import { HttpClient, HttpParams, HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { environment } from '@environment';
import { owningTenantContext } from '@modules/common/interceptors/spaces.interceptor';
import { type Note } from '../types/note';
import { type Observable } from 'rxjs';

export interface NoteVersion {
  readonly version: number;
  readonly kind: 'created' | 'baseline' | 'edited' | 'restored';
  readonly author: { readonly id: string; readonly name: string | null } | null;
  readonly at: string;
  readonly fields: readonly string[];
  readonly restoredFrom: number | null;
  readonly review: 'requested' | 'approved' | 'changes_requested' | null;
}
export interface NoteVersionsPage {
  readonly items: readonly NoteVersion[];
  readonly current: number | null;
  readonly total: number;
}
export interface SavedNoteVersion extends NoteVersion {
  readonly title: string;
  readonly description: string | null;
  readonly body: string;
}
export interface NoteRestoreAnswer {
  readonly item: Note;
  readonly version: number;
  readonly restored: boolean;
}

/** Local to the view, retaining its workspace-scoped HTTP client. */
@Injectable()
export class NoteVersionsClient {
  private readonly http = inject(HttpClient);

  public list(note: Pick<Note, 'id' | 'accountId'>, offset: number): Observable<NoteVersionsPage> {
    return this.http.get<NoteVersionsPage>(this.url(note), {
      params: new HttpParams().set('offset', offset).set('limit', 25),
      context: owningTenantContext(note.accountId),
    });
  }
  public read(note: Pick<Note, 'id' | 'accountId'>, version: number): Observable<SavedNoteVersion> {
    return this.http.get<SavedNoteVersion>(`${this.url(note)}/${version}`, {
      context: owningTenantContext(note.accountId),
    });
  }
  public restore(note: Pick<Note, 'id' | 'accountId'>, version: number): Observable<NoteRestoreAnswer> {
    return this.http.post<NoteRestoreAnswer>(`${this.url(note)}/${version}/restore`, null, {
      context: owningTenantContext(note.accountId),
    });
  }
  private url(note: Pick<Note, 'id' | 'accountId'>): string {
    return `${environment.baseUrl}/api/notes/${encodeURIComponent(note.id)}/versions`;
  }
}

export function noteVersionError(error: unknown): string {
  if (error instanceof HttpErrorResponse) {
    const body: unknown = error.error;
    if (body && typeof body === 'object' && 'message' in body && typeof body.message === 'string') return body.message;
  }
  return 'Version history could not load. Try again.';
}
